import {
  IDocument,
  IField,
  IOriginalFieldState,
  IParentType,
  ITypeFragment,
  PrimitiveDocument,
} from '../../models/datamapper';
import { IChoiceSelection, IFieldSubstitution, IFieldTypeOverride } from '../../models/datamapper/metadata';
import { FieldOverrideVariant, IFieldSubstituteInfo, Types } from '../../models/datamapper/types';
import { QName } from '../../xml-schema-ts/QName';
import { SchemaPathService } from '../schema-path.service';

export type ParseTypeOverrideFn = (
  typeString: string,
  namespaceMap: Record<string, string>,
  field: IField,
) => { type: Types; typeQName: QName; variant: FieldOverrideVariant };

export type ResolveSubstituteFn = (
  document: IDocument,
  substitution: IFieldSubstitution,
  namespaceMap: Record<string, string>,
) => IFieldSubstituteInfo | undefined;

type OverrideItem =
  | { kind: 'substitution'; item: IFieldSubstitution }
  | { kind: 'typeOverride'; item: IFieldTypeOverride }
  | { kind: 'choiceSelection'; item: IChoiceSelection };

/**
 * The collection of utility functions shared among {@link DocumentService}, {@link XmlSchemaDocumentService}
 * and {@link JsonSchemaDocumentService} while avoiding circular dependency.
 *
 * @see DocumentService
 * @see XmlSchemaDocumentService
 * @see JsonSchemaDocumentService
 */
export class DocumentUtilService {
  /**
   * Returns the owner document for the given field or document.
   * If the argument is a field, its {@link IField.ownerDocument} is returned; otherwise the argument itself is returned.
   * @param docOrField - A field or document
   * @returns The owner document cast to the specified DocumentType
   */
  static getOwnerDocument<DocumentType extends IDocument>(docOrField: IParentType): DocumentType {
    return ('ownerDocument' in docOrField ? docOrField.ownerDocument : docOrField) as DocumentType;
  }

  /**
   * Resolves the currently selected member from a wrapper field.
   * Choice wrappers use positional selection ({@link IField.selectedMemberIndex}).
   * Abstract wrappers use name-based selection ({@link IField.selectedMemberQName})
   * because their candidate set is open-ended and must survive wrapper child rebuilds.
   */
  static getSelectedMember(field: IField): IField | undefined {
    if (field.wrapperKind === 'choice') {
      return field.selectedMemberIndex === undefined ? undefined : field.fields?.[field.selectedMemberIndex];
    }
    if (field.wrapperKind === 'abstract' && field.selectedMemberQName) {
      return field.fields?.find(
        (child) =>
          child.name === field.selectedMemberQName!.getLocalPart() &&
          child.namespaceURI === field.selectedMemberQName!.getNamespaceURI(),
      );
    }
    return undefined;
  }

  /**
   * Resolve type fragments from reference and populate into the document tree so that it could be expanded in the UI.
   *
   *  @TODO is it safe to change field type dynamically even on XML field? we might eventually need to readahead field type
   *  even for XML field just like JSON field does, see {@link JsonSchemaDocumentService.createJsonSchemaDocument}
   * @param field - The field to resolve type fragments for
   */
  static resolveTypeFragment(field: IField): IField {
    if (field.namedTypeFragmentRefs.length === 0) return field;
    field.originalField ??= DocumentUtilService.captureOriginalFieldState(field);
    const doc = DocumentUtilService.getOwnerDocument(field);
    const unresolvedRefs: string[] = [];
    for (const ref of field.namedTypeFragmentRefs) {
      const fragment = doc.namedTypeFragments[ref];
      if (!fragment) {
        unresolvedRefs.push(ref);
        continue;
      }
      unresolvedRefs.push(...DocumentUtilService.adoptTypeFragment(field, fragment));
    }
    field.namedTypeFragmentRefs = unresolvedRefs;
    if (
      field.typeOverride === FieldOverrideVariant.NONE &&
      field.originalField.fields === undefined &&
      field.fields.length > 0
    ) {
      field.originalField.fields = [...field.fields];
      field.originalField.namedTypeFragmentRefs = unresolvedRefs;
    }
    return field;
  }

  private static captureOriginalFieldState(field: IField): IOriginalFieldState {
    return {
      name: field.name,
      displayName: field.displayName,
      namespaceURI: field.namespaceURI,
      namespacePrefix: field.namespacePrefix,
      type: field.type,
      typeQName: field.typeQName,
      namedTypeFragmentRefs: [...field.namedTypeFragmentRefs],
      fields: field.fields.length > 0 ? [...field.fields] : undefined,
      isAbstractType: field.isAbstractType,
    };
  }

  /**
   * Merges a {@link ITypeFragment} into a field, adopting its type, occurrence constraints, and child fields.
   * Recursively resolves and adopts any nested type fragment references from the owner document.
   * @param field - The field to adopt the fragment into
   * @param fragment - The type fragment whose properties and children will be merged into the field
   */
  static adoptTypeFragment(field: IField, fragment: ITypeFragment): string[] {
    const doc = DocumentUtilService.getOwnerDocument(field);
    const unresolved: string[] = [];
    if (fragment.type) field.type = fragment.type;
    if (fragment.minOccurs !== undefined) field.minOccurs = fragment.minOccurs;
    if (fragment.maxOccurs !== undefined) field.maxOccurs = fragment.maxOccurs;
    for (const f of fragment.fields) f.adopt(field);
    for (const childRef of fragment.namedTypeFragmentRefs) {
      const childFragment = doc.namedTypeFragments[childRef];
      if (!childFragment) {
        unresolved.push(childRef);
        continue;
      }
      unresolved.push(...DocumentUtilService.adoptTypeFragment(field, childFragment));
    }
    return unresolved;
  }

  /**
   * Builds the ancestor chain of a field, walking up through parent references to the document root.
   * @param field - The field to build the ancestor stack for
   * @param includeItself - If true, the field itself is prepended to the returned array
   * @returns Array of ancestor fields ordered from the field (or its parent) up to the document root
   */
  static getFieldStack(field: IField, includeItself: boolean = false): IField[] {
    if (field instanceof PrimitiveDocument) return [];
    const fieldStack: IField[] = [];
    if (includeItself) fieldStack.push(field);
    for (let next = field.parent; 'parent' in next && next !== next.parent; next = next.parent) {
      fieldStack.push(next);
    }
    return fieldStack;
  }

  /**
   * Performs an in-place mutation of a non-abstract field, overwriting its wire name, type,
   * namespace, and fragment refs with the substitute element's metadata.
   * Only used for non-abstract fields — abstract wrapper fields use `selectedMemberQName`
   * instead. See {@link processFieldSubstitution} for the branching logic.
   *
   * @param field - The field to apply the substitution to
   * @param info - The resolved substitute element info
   */
  static applySubstitutionToField(field: IField, info: IFieldSubstituteInfo): void {
    field.originalField ??= DocumentUtilService.captureOriginalFieldState(field);
    field.name = info.qname.getLocalPart()!;
    field.displayName = info.displayName;
    field.namespaceURI = info.qname.getNamespaceURI();
    field.namespacePrefix = info.qname.getPrefix();
    field.type = info.type;
    field.typeQName = info.typeQName;
    field.namedTypeFragmentRefs = [...info.namedTypeFragmentRefs];
    field.typeOverride = FieldOverrideVariant.SUBSTITUTION;
    field.isAbstractType = undefined;
    field.fields = [];

    if (info.type === Types.Container && field.namedTypeFragmentRefs.length > 0) {
      DocumentUtilService.resolveTypeFragment(field);
    }
  }

  /**
   * Applies a single field substitution to a document.
   *
   * Two distinct paths exist depending on the target field:
   * - **Abstract wrapper fields** (`wrapperKind='abstract'`): the wrapper already holds all concrete
   *   substitution candidates as children (populated by the parser). Applying a substitution
   *   finds the matching candidate by name and sets `selectedMemberQName` on the wrapper —
   *   the field tree is not mutated.
   * - **Non-abstract fields**: the field is directly mutated via {@link applySubstitutionToField},
   *   which replaces the field's name, type, namespace, and children with the substitute
   *   element's metadata.
   *
   * @param document - The document to apply the substitution to
   * @param substitution - The substitution metadata
   * @param namespaceMap - Namespace prefix to URI mapping for path resolution
   * @param resolveSubstituteFn - Format-specific function to resolve the substitute element info
   */
  static processFieldSubstitution(
    document: IDocument,
    substitution: IFieldSubstitution,
    namespaceMap: Record<string, string>,
    resolveSubstituteFn: ResolveSubstituteFn,
  ): void {
    const field = SchemaPathService.navigateToField(document, substitution.schemaPath, namespaceMap);
    if (!field) return;

    if (field.wrapperKind === 'abstract') {
      const info = resolveSubstituteFn(document, substitution, namespaceMap);
      if (!info) return;
      const candidate = field.fields.some(
        (f) => f.name === info.qname.getLocalPart() && f.namespaceURI === info.qname.getNamespaceURI(),
      );
      if (candidate) {
        field.selectedMemberQName = info.qname;
      }
      return;
    }

    const info = resolveSubstituteFn(document, substitution, namespaceMap);
    if (info) {
      DocumentUtilService.applySubstitutionToField(field, info);
    }
  }

  /**
   * Low level API to apply multiple field type overrides to a document.
   * UI component should use {@link FieldOverrideService.applyFieldTypeOverride()}.
   *
   * Iterates through all provided overrides and applies them to the document.
   * Each override modifies both the field in the document tree and updates
   * the DocumentDefinition to keep them in sync.
   *
   * @param document - The document to apply overrides to
   * @param overrides - Array of field type overrides to apply
   * @param namespaceMap - Namespace prefix to URI mapping for path resolution
   * @param parseTypeOverride - Function to parse type override strings
   *
   * @example
   * ```typescript
   * const overrides = [
   *   { schemaPath: '/ns0:ShipOrder/ns0:OrderPerson', type: 'xs:int', originalType: 'xs:string', variant: FieldOverrideVariant.FORCE },
   *   { schemaPath: '/ns0:ShipOrder/ShipTo/City', type: 'xs:boolean', originalType: 'xs:string', variant: FieldOverrideVariant.FORCE }
   * ];
   * DocumentUtilService.processTypeOverrides(
   *   document,
   *   overrides,
   *   namespaceMap,
   *   XmlSchemaTypesService.parseTypeOverride
   * );
   * ```
   *
   * @see processTypeOverride
   * @see removeTypeOverride
   */
  static processTypeOverrides(
    document: IDocument,
    overrides: IFieldTypeOverride[],
    namespaceMap: Record<string, string>,
    parseTypeOverride: ParseTypeOverrideFn,
  ): void {
    for (const override of overrides) {
      const field = SchemaPathService.navigateToField(document, override.schemaPath, namespaceMap);
      if (field) {
        try {
          DocumentUtilService.applyTypeOverrideToField(field, override.type, namespaceMap, parseTypeOverride);
        } catch {
          // Skip stale overrides with unresolvable namespace prefixes
        }
      }
    }
  }

  /**
   * Low level API to apply a field type override to a document.
   * UI component should use {@link FieldOverrideService.applyFieldTypeOverride()}.
   *
   * Navigates to the specified field using schema path and changes its type. Also updates
   * the DocumentDefinition.fieldTypeOverrides to keep the definition in sync with
   * the live document. This ensures that when the document is recreated from the
   * definition, the override will be reapplied.
   *
   * @param document - The document to apply the override to
   * @param override - The field type override to apply
   * @param namespaceMap - Namespace prefix to URI mapping for path resolution
   * @param parseTypeOverride - Function to parse type override strings (format-specific)
   *
   * @example
   * ```typescript
   * const override: IFieldTypeOverride = {
   *   schemaPath: '/ns0:ShipOrder/ns0:OrderPerson',
   *   type: 'xs:int',
   *   originalType: 'xs:string',
   *   variant: FieldOverrideVariant.FORCE,
   * };
   * DocumentUtilService.processTypeOverride(
   *   document,
   *   override,
   *   namespaceMap,
   *   XmlSchemaTypesService.parseTypeOverride
   * );
   * // Field is now type xs:int and override is stored in document.definition.fieldTypeOverrides
   * ```
   *
   * @remarks
   * This method modifies both the runtime field structure and the DocumentDefinition:
   * 1. Runtime: Changes field.type, field.typeQName, field.typeOverride, clears field.fields
   * 2. Definition: Adds/updates override in document.definition.fieldTypeOverrides
   *
   * For retrieving type override candidates, use {@link FieldOverrideService.getSafeOverrideCandidates}
   * or {@link FieldOverrideService.getAllOverrideCandidates}.
   *
   * @see processTypeOverrides
   * @see removeTypeOverride
   * @see FieldOverrideService.getSafeOverrideCandidates
   * @see FieldOverrideService.getAllOverrideCandidates
   */
  static processTypeOverride(
    document: IDocument,
    override: IFieldTypeOverride,
    namespaceMap: Record<string, string>,
    parseTypeOverride: ParseTypeOverrideFn,
  ): boolean {
    const field = SchemaPathService.navigateToField(document, override.schemaPath, namespaceMap);
    if (!field) return false;

    try {
      DocumentUtilService.applyTypeOverrideToField(field, override.type, namespaceMap, parseTypeOverride);
    } catch {
      return false;
    }

    document.definition.fieldTypeOverrides ??= [];
    const existingIndex = document.definition.fieldTypeOverrides.findIndex((o) => o.schemaPath === override.schemaPath);
    if (existingIndex >= 0) {
      document.definition.fieldTypeOverrides[existingIndex] = override;
    } else {
      document.definition.fieldTypeOverrides.push(override);
    }
    return true;
  }

  /**
   * Low level API to remove a field type override from a document.
   * UI component should use {@link FieldOverrideService.revertFieldTypeOverride()}.
   *
   * Removes the override for the specified field path and restores the field to its
   * original type in the live document. Also updates the DocumentDefinition to keep
   * the definition in sync with the live document.
   *
   * This method is symmetric with {@link processTypeOverride}: both update the field
   * in the live document and keep the DocumentDefinition in sync.
   *
   * @param document - The document to remove the override from
   * @param schemaPath - Schema path string identifying the field (e.g., '/ns0:ShipOrder/ns0:OrderPerson')
   * @param namespaceMap - Namespace prefix to URI mapping for path resolution
   *
   * @example
   * ```typescript
   * // Remove the override and restore field to original type
   * DocumentUtilService.removeTypeOverride(
   *   document,
   *   '/ns0:ShipOrder/ns0:OrderPerson',
   *   namespaceMap
   * );
   * // Field is now restored to its original type without recreating the document
   * ```
   *
   * @remarks
   * This method modifies both the runtime field structure and the DocumentDefinition:
   * 1. Runtime: Restores field.type, field.typeQName, field.typeOverride, clears field.fields
   * 2. Definition: Removes override from document.definition.fieldTypeOverrides
   *
   * @see processTypeOverride
   * @see processTypeOverrides
   */
  static removeTypeOverride(document: IDocument, schemaPath: string, namespaceMap: Record<string, string>): boolean {
    if (!document.definition.fieldTypeOverrides) return false;

    const override = document.definition.fieldTypeOverrides.find((o) => o.schemaPath === schemaPath);
    if (!override) return false;

    const field = SchemaPathService.navigateToField(document, schemaPath, namespaceMap);
    if (field) {
      DocumentUtilService.restoreOriginalField(field);
    }

    document.definition.fieldTypeOverrides = document.definition.fieldTypeOverrides.filter(
      (o) => o.schemaPath !== schemaPath,
    );
    return true;
  }

  /**
   * Apply a type override to a specific field.
   *
   * Changes the field's type, typeQName, and typeOverride properties. Clears
   * existing child fields and sets up namedTypeFragmentRefs for Container types.
   *
   * @param field - The field to apply the override to
   * @param typeString - The type string to parse and apply
   * @param namespaceMap - Namespace prefix to URI mapping
   * @param parseTypeOverride - Function to parse type override strings
   */
  private static applyTypeOverrideToField(
    field: IField,
    typeString: string,
    namespaceMap: Record<string, string>,
    parseTypeOverride: ParseTypeOverrideFn,
  ): void {
    field.originalField ??= DocumentUtilService.captureOriginalFieldState(field);

    const { type, typeQName, variant } = parseTypeOverride(typeString, namespaceMap, field);

    field.type = type;
    field.typeQName = typeQName;
    field.typeOverride = variant;
    field.isAbstractType = undefined;
    field.fields = [];

    if (type === Types.Container) {
      field.namedTypeFragmentRefs = [typeQName.toString()];
      // Eagerly resolve type fragment so children are immediately available
      DocumentUtilService.resolveTypeFragment(field);
    } else {
      field.namedTypeFragmentRefs = [];
    }
  }

  /**
   * Restore a field to its original state before any override or substitution.
   *
   * Reverses a type override or element substitution by restoring the field's
   * name, namespace, type, and child fields to their captured original values.
   * For Container types whose children were not captured, eagerly resolves
   * type fragments so children are immediately available.
   *
   * @param field - The field to restore
   */
  static restoreOriginalField(field: IField): void {
    const origType = field.originalField?.type ?? field.type;
    const origTypeQName = field.originalField?.typeQName ?? field.typeQName;
    const origRefs = field.originalField?.namedTypeFragmentRefs;
    const origFields = field.originalField?.fields;

    field.type = origType;
    field.typeQName = origTypeQName;
    if (field.originalField) {
      field.name = field.originalField.name;
      field.displayName = field.originalField.displayName;
      field.namespaceURI = field.originalField.namespaceURI;
      field.namespacePrefix = field.originalField.namespacePrefix;
      field.isAbstractType = field.originalField.isAbstractType;
    }
    field.typeOverride = FieldOverrideVariant.NONE;
    field.originalField = undefined;

    field.fields = origFields ?? [];
    if (origRefs !== undefined) {
      field.namedTypeFragmentRefs = [...origRefs];
    } else if (!origFields && origType === Types.Container && origTypeQName) {
      field.namedTypeFragmentRefs = [origTypeQName.toString()];
      // Eagerly resolve type fragment so children are immediately available
      DocumentUtilService.resolveTypeFragment(field);
    } else {
      field.namedTypeFragmentRefs = [];
    }
    for (const child of field.fields) {
      if (child.typeOverride !== FieldOverrideVariant.NONE) {
        DocumentUtilService.restoreOriginalField(child);
      }
    }
  }

  /**
   * Low level API to apply multiple choice selections to a document.
   * Iterates through all provided selections and delegates to {@link processChoiceSelection} for each.
   *
   * @param document - The document to apply selections to
   * @param selections - Array of choice selections to apply
   * @param namespaceMap - Namespace prefix to URI mapping for path resolution
   *
   * @see processChoiceSelection
   * @see removeChoiceSelection
   */
  static processChoiceSelections(
    document: IDocument,
    selections: IChoiceSelection[],
    namespaceMap: Record<string, string>,
  ): void {
    for (const selection of selections) {
      DocumentUtilService.processChoiceSelection(document, selection, namespaceMap);
    }
  }

  /**
   * Low level API to apply a single choice selection to a document and update its definition.
   * Navigates to the choice field using the schemaPath and sets its {@link IField.selectedMemberIndex}.
   * Also updates {@link DocumentDefinition.choiceSelections} to keep the definition in sync with the live document.
   *
   * @param document - The document to apply the selection to
   * @param selection - The choice selection containing the path and selected member index
   * @param namespaceMap - Namespace prefix to URI mapping for path resolution
   *
   * @see processChoiceSelections
   * @see removeChoiceSelection
   */
  static processChoiceSelection(
    document: IDocument,
    selection: IChoiceSelection,
    namespaceMap: Record<string, string>,
  ): boolean {
    const choiceField = SchemaPathService.navigateToField(document, selection.schemaPath, namespaceMap);
    if (choiceField?.wrapperKind !== 'choice') return false;

    const applied = DocumentUtilService.applyChoiceSelectionToField(choiceField, selection.selectedMemberIndex);
    if (!applied) return false;

    document.definition.choiceSelections ??= [];
    const existingIndex = document.definition.choiceSelections.findIndex((s) => s.schemaPath === selection.schemaPath);
    if (existingIndex >= 0) {
      document.definition.choiceSelections[existingIndex] = selection;
    } else {
      document.definition.choiceSelections.push(selection);
    }
    return true;
  }

  /**
   * Low level API to remove a choice selection from a document and update its definition.
   * Navigates to the choice field and clears its {@link IField.selectedMemberIndex}.
   * Also removes the selection from {@link DocumentDefinition.choiceSelections}.
   *
   * @param document - The document to remove the selection from
   * @param schemaPath - The path string identifying the choice field (uses {@link IChoiceSelection.schemaPath} format)
   * @param namespaceMap - Namespace prefix to URI mapping for path resolution
   *
   * @see processChoiceSelection
   * @see processChoiceSelections
   */
  static removeChoiceSelection(document: IDocument, schemaPath: string, namespaceMap: Record<string, string>): boolean {
    if (!document.definition.choiceSelections) return false;

    const existingIndex = document.definition.choiceSelections.findIndex((s) => s.schemaPath === schemaPath);
    if (existingIndex < 0) return false;

    const choiceField = SchemaPathService.navigateToField(document, schemaPath, namespaceMap);
    if (choiceField?.wrapperKind === 'choice') {
      choiceField.selectedMemberIndex = undefined;
    }

    document.definition.choiceSelections = document.definition.choiceSelections.filter(
      (s) => s.schemaPath !== schemaPath,
    );
    return true;
  }

  private static applyChoiceSelectionToField(choiceField: IField, selectedMemberIndex: number): boolean {
    if (selectedMemberIndex < 0 || selectedMemberIndex >= choiceField.fields.length) {
      // Ignoring out of length selectedMemberIndex
      return false;
    }
    if (choiceField.selectedMemberIndex === selectedMemberIndex) {
      return false;
    }
    choiceField.selectedMemberIndex = selectedMemberIndex;
    return true;
  }

  /**
   * Unified depth-ordered initialization of substitutions, type overrides, and choice selections.
   *
   * Combines all three kinds, sorts by schema path depth (shallower first).
   * At the same depth the priority order is: substitution < typeOverride < choiceSelection.
   * This ordering ensures parent substitutions and overrides are applied before descendant
   * choice selections, preventing stale navigation when a substitution or type override collapses a subtree.
   *
   * @param document - The document to apply overrides and selections to
   * @param typeOverrides - Array of field type overrides to apply
   * @param choiceSelections - Array of choice selections to apply
   * @param fieldSubstitutions - Array of field substitutions to apply
   * @param namespaceMap - Namespace prefix to URI mapping for path resolution
   * @param parseTypeOverride - Function to parse type override strings
   * @param resolveSubstituteFn - Format-specific function to resolve substitute element info
   */
  static processOverrides(
    document: IDocument,
    typeOverrides: IFieldTypeOverride[],
    choiceSelections: IChoiceSelection[],
    fieldSubstitutions: IFieldSubstitution[],
    namespaceMap: Record<string, string>,
    parseTypeOverride: ParseTypeOverrideFn,
    resolveSubstituteFn: ResolveSubstituteFn,
  ): void {
    const kindPriority: Record<string, number> = { substitution: 0, typeOverride: 1, choiceSelection: 2 };
    const items: OverrideItem[] = [
      ...fieldSubstitutions.map((item): OverrideItem => ({ kind: 'substitution', item })),
      ...typeOverrides.map((item): OverrideItem => ({ kind: 'typeOverride', item })),
      ...choiceSelections.map((item): OverrideItem => ({ kind: 'choiceSelection', item })),
    ];

    items.sort((a, b) => {
      const depthA = a.item.schemaPath.split('/').filter(Boolean).length;
      const depthB = b.item.schemaPath.split('/').filter(Boolean).length;
      if (depthA !== depthB) return depthA - depthB;
      return kindPriority[a.kind] - kindPriority[b.kind];
    });

    for (const tagged of items) {
      if (tagged.kind === 'substitution') {
        DocumentUtilService.processFieldSubstitution(document, tagged.item, namespaceMap, resolveSubstituteFn);
      } else if (tagged.kind === 'typeOverride') {
        DocumentUtilService.processTypeOverride(document, tagged.item, namespaceMap, parseTypeOverride);
      } else {
        DocumentUtilService.processChoiceSelection(document, tagged.item, namespaceMap);
      }
    }
  }

  /**
   * Removes all field type overrides and choice selections whose schema path starts with
   * `schemaPath + "/"` (i.e. strict descendants of the given path).
   * The entry at `schemaPath` itself is preserved.
   *
   * Call this after applying a type override or changing a choice selection to invalidate
   * stale descendant overrides and selections whose ancestor has changed.
   *
   * @param document - The document whose definition will be pruned
   * @param schemaPath - The schema path of the changed field; all descendants will be removed
   */
  static invalidateDescendants(document: IDocument, schemaPath: string): void {
    const prefix = schemaPath + '/';
    if (document.definition.fieldTypeOverrides) {
      document.definition.fieldTypeOverrides = document.definition.fieldTypeOverrides.filter(
        (o) => !o.schemaPath.startsWith(prefix),
      );
    }
    if (document.definition.choiceSelections) {
      document.definition.choiceSelections = document.definition.choiceSelections.filter(
        (s) => !s.schemaPath.startsWith(prefix),
      );
    }
    if (document.definition.fieldSubstitutions) {
      document.definition.fieldSubstitutions = document.definition.fieldSubstitutions.filter(
        (s) => !s.schemaPath.startsWith(prefix),
      );
    }
  }
}
